<template>
  <div class="edit-text-overlay" v-show="showOverlay" @click="handleDeselectTextInput">
    <div class="edit-text-overlay__controls" v-show="applicationStore.currentEditingText?.text">
      <text-control-button text="Delete" @click="onDelete">
        <delete-icon class="edit-text-overlay__controls-icon" />
      </text-control-button>
      <text-control-button text="Done" @click="onDone">
        <done-icon class="edit-text-overlay__controls-icon" />
      </text-control-button>
    </div>
    <span
      ref="textArea"
      class="edit-text-overlay__text"
      contenteditable
      :style="applicationStore.currentEditingText?.cssStyle"
      @input="onInput"
      @paste.prevent="onPaste"
      @keydown.enter.exact.prevent="onDone"
    />
  </div>
</template>
<script setup lang="ts">
import { nextTick, ref, toRefs, watch } from 'vue'
import TextControlButton from '@/components/preview/TextControlButton.vue'
import DeleteIcon from '@/assets/DeleteIcon.svg'
import DoneIcon from '@/assets/DoneIcon.svg'
import { useProjectStore } from '@/store/project-store'
import { useApplicationStore } from '@/store/application-store'
import useTextStates from '@/composables/UseTextStates'

const projectStore = useProjectStore()
const applicationStore = useApplicationStore()

const props = defineProps<{
  showOverlay: boolean
}>()

const { showOverlay } = toRefs(props)
const textArea = ref<HTMLSpanElement | null>(null)

const {
  addText,
  saveText,
  deleteText,
  getMaxWidth
} = useTextStates()

const onInput = () => {
  if (textArea.value && applicationStore.currentEditingText) {
    // remove images
    const imageRegex = /<img[^>]*>/g
    if (textArea.value.innerHTML.match(imageRegex)) {
      textArea.value.innerHTML = textArea.value.innerHTML.replace(imageRegex, '')
      setCaretAtEndOfText()
    }

    // update the text with a new value
    applicationStore.currentEditingText.text = textArea.value.innerText
    if (applicationStore.editTextState === 'adding') {
      applicationStore.currentEditingText.x = textArea.value.offsetLeft + textArea.value.offsetWidth / 2
      applicationStore.currentEditingText.y = textArea.value.offsetTop + textArea.value.offsetHeight / 2
    }
  }
}

const onPaste = (event: ClipboardEvent) => {
  if (textArea.value && event.clipboardData) {
    textArea.value.innerHTML = event.clipboardData.getData('text/plain')
    onInput()
    setCaretAtEndOfText()
  }
}

const onDelete = () => {
  if (textArea.value) {
    if (projectStore.textStates.length === 0 && applicationStore.editTextState === 'adding') {
      // Clear text
      textArea.value.innerText = ''
      textArea.value.focus()
    } else {
      if (applicationStore.currentEditingText) {
        deleteText(applicationStore.currentEditingText)

        if (projectStore.textStates.length > 0) {
          // Set the selected text to be the last one added
          applicationStore.selectText(projectStore.textStates[projectStore.textStates.length - 1])
        } else {
          // Reset to new text state
          textArea.value.innerText = ''
          addText({ style: applicationStore.selectedText?.style })
        }
      }
    }
  }
}

const onDone = () => {
  // should never happen
  if (textArea.value === null) {
    return
  }

  // get text length excluding whitespace and newlines
  const strLength = textArea.value.innerText.trim().length

  // if there is no valid text, delete whitespace and newlines and set the caret at the end
  if (strLength === 0) {
    textArea.value.innerText = ''
    setCaretAtEndOfText()
    return
  }

  if (applicationStore.currentEditingText) {
    saveText(applicationStore.currentEditingText)
    textArea.value.innerText = ''
  }
}

const handleDeselectTextInput = (event: Event) => {
  if (event.target === event.currentTarget) {
    // Save when user clicks off target
    onDone()
  }
}

const setCaretAtEndOfText = () => {
  if (textArea.value) {
    const range = document.createRange()
    range.selectNodeContents(textArea.value)
    range.collapse(false)

    const selection = window.getSelection()
    if (selection) {
      selection.removeAllRanges()
      selection.addRange(range)
    }
  }
}

watch(showOverlay, async () => {
  if (!showOverlay.value) {
    onDone()
    return
  }
  
  if (textArea.value && applicationStore.currentEditingText) {
    textArea.value.innerText = applicationStore.currentEditingText.text
    await nextTick()
    setCaretAtEndOfText()
    applicationStore.currentEditingText.style.maxWidth = getMaxWidth()
    applicationStore.selectedText = applicationStore.selectedText ?? applicationStore.currentEditingText
  }
})

watch(() => projectStore.resolution, () => {
  if (applicationStore.currentEditingText && applicationStore.editTextState !== 'none') {
    applicationStore.currentEditingText.style.maxWidth = getMaxWidth()
  }
})

</script>

<style lang="scss">
// Parts of this component is scaled up to match the canvas size, all defined pixel dimensions below are scaled up
.edit-text-overlay {
  height: inherit;
  width: inherit;
  background: rgba($black, 0.5);

  display: flex;
  flex-direction: column;
  gap: calc(16px / var(--canvas-scale));
  align-items: center;
  justify-content: center;
  padding: 15%;

  &__controls {
    position: absolute;
    top: 50%; 
    left: 100%;
    margin-left: calc(20px / var(--canvas-scale));
    display: flex;
    flex-direction: column;
    gap: 10px;
    transform: scale(calc(1 / var(--canvas-scale)), calc(1 / var(--canvas-scale))) translateY(-50%);
    transform-origin: left top;
  }

  &__text {
    display: inline-block;
    font-size: 100px;
    line-height: 100px;
    white-space: break-spaces;
    overflow-wrap: break-word;

    &:empty::before {
      content: "Start typing...";
      color: $grey-50;
    }
  }
}
</style>
